import { useNavigate } from "@tanstack/react-router";
import { ClipboardCheck } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { ResizableTh } from "@/components/shared/resizable-th";
import { Badge } from "@/components/ui/badge";
import { useAudits } from "@/hooks/use-audits";
import type { ResizableColumn } from "@/hooks/use-resizable-columns";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { AUDIT_STATUS_BADGE_VARIANTS, AUDIT_STATUS_LABELS } from "@/lib/audit-status";
import { formatDate } from "@/lib/format";

const COLUMNS: ResizableColumn[] = [
  { key: "template", defaultWidth: 280 },
  { key: "assignee", defaultWidth: 150 },
  { key: "progress", defaultWidth: 170 },
  { key: "lastUpdated", defaultWidth: 130 },
  { key: "status", defaultWidth: 120 },
];

function ProgressBar({ checked, total }: { checked: number; total: number }) {
  const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-8 text-right">{pct}%</span>
    </div>
  );
}

export function ProjectAudits({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { data: allAudits, isLoading, isError, refetch } = useAudits();
  const { widths, startResize } = useResizableColumns("project-audits", COLUMNS);

  if (isError) {
    return <ErrorState message="Failed to load audits" onRetry={() => refetch()} />;
  }

  const audits = (allAudits ?? []).filter((a) => a.projectId === projectId);

  if (isLoading) {
    return (
      <div className="rounded-md border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              {["Template", "Assignee", "Progress", "Last Updated", "Status"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {["sk-1", "sk-2", "sk-3"].map((rk) => (
              <tr key={rk} className="border-b border-border last:border-0">
                {["c1", "c2", "c3", "c4", "c5"].map((ck) => (
                  <td key={ck} className="px-4 py-3">
                    <div className="h-4 bg-muted rounded animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (audits.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="No audits for this project"
        description="Audits assigned to this project will appear here."
      />
    );
  }

  return (
    <div className="rounded-md border border-border overflow-x-auto">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <ResizableTh width={widths.template} onResizeStart={startResize("template")}>
              Template
            </ResizableTh>
            <ResizableTh
              width={widths.assignee}
              onResizeStart={startResize("assignee")}
              className="hidden md:table-cell"
            >
              Assignee
            </ResizableTh>
            <ResizableTh width={widths.progress} onResizeStart={startResize("progress")}>
              Progress
            </ResizableTh>
            <ResizableTh
              width={widths.lastUpdated}
              onResizeStart={startResize("lastUpdated")}
              className="hidden lg:table-cell"
            >
              Last Updated
            </ResizableTh>
            <ResizableTh width={widths.status} onResizeStart={startResize("status")}>
              Status
            </ResizableTh>
          </tr>
        </thead>
        <tbody>
          {audits.map((audit) => (
            <tr
              key={audit.id}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
              onClick={() =>
                navigate({
                  to: "/dashboard/audits/$auditId",
                  params: { auditId: audit.id },
                })
              }
            >
              <td className="px-4 py-3 font-medium">
                {audit.templates.map((t) => t.name).join(", ") || "—"}
              </td>
              <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                {audit.assignedToName ?? "—"}
              </td>
              <td className="px-4 py-3">
                <ProgressBar checked={audit.checkedItems} total={audit.totalItems} />
              </td>
              <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">
                {formatDate(audit.updatedAt)}
              </td>
              <td className="px-4 py-3">
                <Badge variant={AUDIT_STATUS_BADGE_VARIANTS[audit.status] ?? "outline"}>
                  {AUDIT_STATUS_LABELS[audit.status] ?? audit.status}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
